/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { t } from '@sportninja/common/i18n'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'
import Helmet from 'src/components/Helmet'
import GameHeaderSection from 'src/components/GameHeaderSection'
import { AutoPlayButton } from './components'
import LiveScoreWidget from './LiveScoreWidget'
import { isValidVideoUrl, isValidStreamingUrl } from 'src/utils/video'
import colors from '@sportninja/common/constants/appColors'
import Icon from 'src/components/Icon'
import BroadcastStateOverlay from 'src/components/BroadcastStateOverlay'
import StreamSelectorDropdown from 'src/components/StreamSelectorDropdown'

const getYoutubeVideoId = (url) => {
  const youtubeUrl = new RegExp(
    '^(https?://)?(www.youtube.com|m.youtube.com|youtu.?be)/.+$'
  )
  if (!url) {
    return ''
  }
  if (!youtubeUrl.test(url)) {
    return ''
  }
  const urlObj = new URL(url)
  const hostname = urlObj.hostname
  if (hostname.includes('youtube.com') || hostname.includes('m.youtube.com')) {
    return urlObj.searchParams.get('v')
  } else if (hostname.includes('youtu.be')) {
    return urlObj.pathname.slice(1) // Remove the leading '/'
  }
  return ''
}

const NewGamePageHeader = ({
  videoUrl,
  broadcastState,
  game,
  organization,
  actions,
  fullGameType,
  isMobile,
  windowWidth,
  onRetry,
  onWatchReplay,
  hasReplayUrl,
  error: broadcastError,
  activeStreamCount = 0,
  onSelectStream,
  selectedStreamType,
  homeTeamName,
  visitorTeamName,
  organizationName,
  streams = [],
  homeTeamImage,
  visitorTeamImage,
}) => {
  const isHostedOnS3 = videoUrl?.includes('s3.amazonaws.com')
  const isStreamingUrl = isValidStreamingUrl(videoUrl)
  const isHlsStream = videoUrl?.toLowerCase()?.endsWith('.m3u8')

  // Compute stream label for the indicator
  const streamLabel = React.useMemo(() => {
    if (!selectedStreamType) {
      return null
    }
    switch (selectedStreamType) {
      case 'home':
        return homeTeamName || 'Home'
      case 'visitor':
        return visitorTeamName || 'Visitor'
      case 'league':
        return organizationName || 'League'
      default:
        return null
    }
  }, [selectedStreamType, homeTeamName, visitorTeamName, organizationName])

  // Use native video element for S3 and streaming URLs (HLS, Mux, etc.)
  const useNativeVideo = isHostedOnS3 || isStreamingUrl
  const [autoPlay, setAutoPlay] = useState(false)
  const [videoError, setVideoError] = useState(false)
  const [isPlayerReady, setIsPlayerReady] = useState(false)
  const isValidVideo = isValidVideoUrl(videoUrl)

  const videoRef = useRef(null)
  const playerRef = useRef(null)
  const hlsRef = useRef(null)

  // Check if thumbnail image is available
  const thumbnailImage = game?.thumbnail_image
  const shouldShowThumbnail = !autoPlay && thumbnailImage

  // Compute props for GameHeaderSection
  const isDesktop = !isMobile
  const title = game.gameNumber
    ? `${t('util:capitalize', { text: 'Game' })} #${game.gameNumber}`
    : game.gameNumber === null || game.gameNumber?.length === 0
    ? t('util:capitalize', { text: 'Game' })
    : `${'Loading'}...`

  const onPlayerStateChange = (event) => {
    if (event.data === window.YT.PlayerState.PLAYING) {
      setAutoPlay(true)
    }
    if (event.data === window.YT.PlayerState.PAUSED) {
      setAutoPlay(false)
    }
  }

  const onPlayerReady = useCallback(
    (event) => {
      console.log('✅ YouTube player ready')
      setIsPlayerReady(true)
      setVideoError(false)
      if (game?.broadcast_video_offset_seconds) {
        console.log(
          'Setting YouTube video offset to:',
          game.broadcast_video_offset_seconds
        )
        event.target.seekTo(game.broadcast_video_offset_seconds, true)
      }
    },
    [game?.broadcast_video_offset_seconds]
  )

  const onPlayerError = (event) => {
    console.error('❌ YouTube player error:', event.data)
    setVideoError(true)
    setIsPlayerReady(false)
  }

  const handleRetryVideo = () => {
    console.log('🔄 handleRetryVideo: Attempting to retry video')
    setVideoError(false)
    setIsPlayerReady(false)
    setAutoPlay(false)
  }

  useEffect(() => {
    if (
      window.YT &&
      !useNativeVideo &&
      videoUrl &&
      isValidVideo &&
      !isPlayerReady &&
      !videoError
    ) {
      try {
        console.log('🎥 Initializing YouTube player')
        playerRef.current = new window.YT.Player('player', {
          height: '720',
          width: windowWidth,
          playerVars: {
            autoplay: 0,
            controls: 1,
            playlist: getYoutubeVideoId(videoUrl),
            rel: 0,
            start: game?.broadcast_video_offset_seconds || 0,
          },
          videoId: videoUrl?.split('v=')[1],
          events: {
            onStateChange: onPlayerStateChange,
            onReady: onPlayerReady,
            onError: onPlayerError,
          },
        })
      } catch (error) {
        console.error('❌ Failed to initialize YouTube player:', error)
        setVideoError(true)
      }
    }
  }, [
    useNativeVideo,
    videoUrl,
    windowWidth,
    game?.broadcast_video_offset_seconds,
    isValidVideo,
    isPlayerReady,
    videoError,
    onPlayerReady,
  ])

  useEffect(() => {
    const playVideo = async () => {
      try {
        if (videoRef.current && autoPlay && !videoError) {
          console.log('🎥 Attempting to play native video')
          await videoRef.current.play()
        }
      } catch (error) {
        console.error('❌ Error playing native video:', error)
        setVideoError(true)
        setAutoPlay(false)
      }
    }

    if (useNativeVideo && !isHlsStream) {
      playVideo()
    }
  }, [videoRef, autoPlay, videoError, useNativeVideo, isHlsStream])

  // Initialize HLS.js for live streaming
  useEffect(() => {
    if (!isHlsStream || !videoUrl || !videoRef.current) {
      return
    }

    // Check if HLS.js is supported (Chrome, Firefox, etc.)
    if (Hls.isSupported()) {
      console.log('🎥 Initializing HLS.js for live stream')
      const hls = new Hls({
        // Live streaming configuration
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 6,
        liveDurationInfinity: true,
        // Start at live edge
        startPosition: -1,
        // Enable low latency mode
        lowLatencyMode: true,
      })

      hls.loadSource(videoUrl)
      hls.attachMedia(videoRef.current)

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('✅ HLS manifest parsed, ready to play')
        setIsPlayerReady(true)

        // Seek to live edge immediately for live streams
        if (broadcastState === 'live' && videoRef.current) {
          const seekable = videoRef.current.seekable
          if (seekable.length > 0) {
            const liveEdge = seekable.end(seekable.length - 1)
            console.log('🔴 Seeking to live edge:', liveEdge)
            videoRef.current.currentTime = liveEdge
          }
        }

        // Auto-play when user clicks play
        if (autoPlay && videoRef.current) {
          videoRef.current.play().catch((err) => {
            console.error('❌ HLS autoplay failed:', err)
          })
        }
      })

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('❌ HLS error:', data)
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log('🔄 HLS network error, trying to recover...')
              hls.startLoad()
              break
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('🔄 HLS media error, trying to recover...')
              hls.recoverMediaError()
              break
            default:
              console.error('❌ Fatal HLS error, cannot recover')
              setVideoError(true)
              hls.destroy()
              break
          }
        }
      })

      hlsRef.current = hls

      return () => {
        console.log('🧹 Cleaning up HLS.js instance')
        hls.destroy()
        hlsRef.current = null
      }
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari has native HLS support
      console.log('🎥 Using native HLS support (Safari)')
      const video = videoRef.current
      video.src = videoUrl

      // Seek to live edge when video loads for live streams
      const handleLoadedData = () => {
        if (broadcastState === 'live' && video) {
          const seekable = video.seekable
          if (seekable.length > 0) {
            const liveEdge = seekable.end(seekable.length - 1)
            console.log('🔴 Safari: Seeking to live edge:', liveEdge)
            video.currentTime = liveEdge
          }
        }
      }
      video.addEventListener('loadeddata', handleLoadedData)
      setIsPlayerReady(true)

      return () => {
        video.removeEventListener('loadeddata', handleLoadedData)
      }
    } else {
      console.error('❌ HLS is not supported in this browser')
      setVideoError(true)
    }
  }, [isHlsStream, videoUrl, autoPlay, broadcastState])

  const handlePlayClick = () => {
    setAutoPlay(true)
    if (playerRef.current) {
      playerRef.current.playVideo()
    }
  }

  // Track previous broadcast state to detect transitions
  const prevBroadcastStateRef = useRef(broadcastState)

  // Auto-play when livestream starts (only on transition to 'live')
  useEffect(() => {
    const wasNotLive = prevBroadcastStateRef.current !== 'live'
    const isNowLive = broadcastState === 'live'

    // Only auto-play when transitioning TO 'live' state
    if (wasNotLive && isNowLive && isValidVideo && !videoError) {
      setAutoPlay(true)
      if (playerRef.current) {
        playerRef.current.playVideo()
      }
    }

    prevBroadcastStateRef.current = broadcastState
  }, [broadcastState, isValidVideo, videoError])

  return (
    <div
      css={css`
        width: ${windowWidth}px;
        height: 720px;
        background: linear-gradient(
          180deg,
          rgba(6, 10, 16, 0.3) 0%,
          #181d26 100%
        );
        position: relative;
        margin-bottom: 16px;
        .ytp-more-videos-view {
          display: none !important;
        }
      `}
    >
      <Helmet title={title} />
      {/* Show overlay for non-live broadcast states (scheduled, ended without recording, error) */}
      {/* If ended WITH recording (videoUrl available), skip overlay and show video player */}
      {broadcastState &&
      broadcastState !== 'live' &&
      broadcastState !== 'idle' &&
      !(broadcastState === 'ended' && videoUrl) ? (
        <BroadcastStateOverlay
          state={broadcastState}
          onRetry={onRetry}
          onWatchReplay={onWatchReplay}
          hasReplayUrl={hasReplayUrl}
          error={broadcastError}
        />
      ) : videoError || !isValidVideo ? (
        <div
          css={css`
            width: 100%;
            height: 720px;
            position: absolute;
            top: 0;
            left: 0;
            background: linear-gradient(
              180deg,
              rgba(6, 10, 16, 0.3) 0%,
              #181d26 100%
            );
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 16px 24px;
              background: rgba(0, 0, 0, 0.7);
              border-radius: 8px;
              backdrop-filter: blur(10px);
            `}
          >
            <Icon
              name='exclamation-triangle'
              fontSize={20}
              color={colors.WARNING}
            />
            <span
              css={css`
                color: ${colors.WHITE};
                font-size: 16px;
                font-weight: 500;
              `}
            >
              {!isValidVideo ? 'Invalid video URL' : 'Video unavailable'}
            </span>
          </div>
          {videoError && (
            <button
              css={css`
                padding: 8px 16px;
                background: ${colors.PRIMARY};
                color: ${colors.WHITE};
                border: none;
                border-radius: 4px;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                transition: background 0.2s;
                &:hover {
                  background: ${colors.PRIMARY_DARK || colors.PRIMARY};
                }
              `}
              onClick={handleRetryVideo}
            >
              Retry
            </button>
          )}
        </div>
      ) : shouldShowThumbnail ? (
        <img
          src={thumbnailImage.full_path}
          alt='Game thumbnail'
          css={css`
            width: 100%;
            height: 720px;
            object-fit: cover;
            position: absolute;
            top: 0;
            left: 0;
            border: none;
          `}
        />
      ) : (
        <>
          {useNativeVideo ? (
            <video
              ref={videoRef}
              // Don't set src for HLS streams - hls.js handles loading
              {...(!isHlsStream && { src: videoUrl || '' })}
              width={windowWidth}
              height='720'
              // eslint-disable-next-line react-native/no-inline-styles
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                border: 'none',
                background:
                  'linear-gradient(180deg, rgba(6, 10, 16, 0.3) 0%, #060a10 100%)',
                // objectFit: 'cover',
              }}
              autoPlay={autoPlay}
              // Only loop S3 videos, not live streams
              loop={isHostedOnS3}
              // Only mute S3 videos by default, live streams should have audio
              muted={isHostedOnS3}
              controls={autoPlay ? true : false}
              // Enable cross-origin for HLS streams only (S3 MP4s don't need it and will fail CORS)
              {...(isHlsStream && { crossOrigin: 'anonymous' })}
              playsInline
              onPause={(e) => {
                // Use a timeout to check if this is a real pause or just seeking
                setTimeout(() => {
                  if (
                    videoRef.current &&
                    videoRef.current.paused &&
                    !videoRef.current.seeking
                  ) {
                    setAutoPlay(false)
                  }
                }, 100)
              }}
              onLoadedData={() => {
                // Set initial video position when video loads (only for S3 videos)
                if (
                  isHostedOnS3 &&
                  game?.broadcast_video_offset_seconds &&
                  videoRef.current
                ) {
                  console.log(
                    'Setting video offset to:',
                    game.broadcast_video_offset_seconds
                  )
                  videoRef.current.currentTime =
                    game.broadcast_video_offset_seconds
                }
              }}
              onPlay={() => {
                setAutoPlay(true)
              }}
              onError={() => {
                console.error('❌ Native video element error')
                setVideoError(true)
              }}
            />
          ) : (
            <div
              id='player'
              // eslint-disable-next-line react-native/no-inline-styles
              style={{
                width: '100%',
                height: 720,
                position: 'absolute',
                top: 0,
                left: 0,
                border: 'none',
                background:
                  'linear-gradient(180deg, rgba(6, 10, 16, 0.3) 0%, #181d26 100%)',
              }}
            />
          )}
        </>
      )}

      {/* Play button overlay - only show when idle or live, not during scheduled/ended/error states */}
      {(!broadcastState ||
        broadcastState === 'idle' ||
        broadcastState === 'live') && (
        <div
          css={css`
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            cursor: pointer;
            ${autoPlay && 'display: none;'}
            ${!useNativeVideo && !shouldShowThumbnail && 'opacity: 0;'}
            ${!videoError && isValidVideo ? '' : 'display: none;'}
          `}
          onClick={handlePlayClick}
        >
          <AutoPlayButton />
        </div>
      )}

      {/* LIVE indicator and stream switch button - show when live or multiple streams */}
      {(broadcastState === 'live' || activeStreamCount >= 2) && (
        <div
          css={css`
            position: absolute;
            top: 16px;
            left: 16px;
            display: flex;
            align-items: center;
            gap: 8px;
            z-index: 10;
          `}
        >
          {/* LIVE indicator with stream label - show when live (regardless of play state) */}
          {broadcastState === 'live' && (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 6px;
                background-color: rgba(0, 0, 0, 0.6);
                padding: 6px 10px;
                border-radius: 4px;
              `}
            >
              <div
                css={css`
                  width: 8px;
                  height: 8px;
                  border-radius: 50%;
                  background-color: ${colors.RED};
                `}
              />
              <span
                css={css`
                  color: ${colors.WHITE};
                  font-size: 12px;
                  font-weight: 700;
                  letter-spacing: 1px;
                `}
              >
                LIVE
              </span>
              {streamLabel && (
                <>
                  <span
                    css={css`
                      color: ${colors.NEUTRAL_50};
                      font-size: 12px;
                    `}
                  >
                    |
                  </span>
                  <span
                    css={css`
                      color: ${colors.WHITE};
                      font-size: 12px;
                      font-weight: 600;
                    `}
                  >
                    {streamLabel}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Switch Stream dropdown - show when multiple streams available (live or recorded) */}
          {activeStreamCount >= 2 && onSelectStream && (
            <StreamSelectorDropdown
              streams={streams}
              selectedStreamType={selectedStreamType}
              onSelectStream={onSelectStream}
              homeTeamName={homeTeamName}
              visitorTeamName={visitorTeamName}
              homeTeamImage={homeTeamImage}
              visitorTeamImage={visitorTeamImage}
              organizationName={organizationName}
              isMobile={false}
            />
          )}
        </div>
      )}

      {/* Live Score Widget - show when video is playing */}
      {autoPlay && !videoError && isValidVideo && (
        <LiveScoreWidget
          game={game}
          homeTeamImage={homeTeamImage}
          visitorTeamImage={visitorTeamImage}
          homeTeamName={homeTeamName}
          visitorTeamName={visitorTeamName}
        />
      )}

      {/* New shared header – absolute-positioned bottom, with container constraints */}
      <div
        css={css`
          position: absolute;
          bottom: 16px;
          left: 50%;
          transform: translateX(-50%);
          width: calc(100% - 36px);
          max-width: 1440px;
          backdrop-filter: blur(80px);
          background: rgba(12, 16, 24, 0.3);
          padding: 8px;
          border-radius: 8px;
          ${autoPlay && 'display: none !important;'}
        `}
      >
        <GameHeaderSection
          css={css`
            position: static !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          `}
          organization={organization}
          game={game}
          title={title}
          fullGameType={fullGameType}
          isDesktop={isDesktop}
        />
      </div>
    </div>
  )
}

export default NewGamePageHeader
